import {
  Alert,
  Details,
  Heading,
  Skeleton,
} from "@digdir/designsystemet-react";
import { useQuery } from "@tanstack/react-query";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type { MalerdataPeriode } from "@/api/generated";
import { getMalerdataPerioderOptions } from "@/api/generated/@tanstack/react-query.gen";
import { MaaledataPeriodeCard } from "./MaalerdataPeriodeCard";

export const MaaledataPerioderListe = ({
  organisasjonsNummer,
}: {
  organisasjonsNummer: number;
}) => {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useQuery({
    ...getMalerdataPerioderOptions({
      client: httpClient,
      path: {
        organisasjonsNummer: organisasjonsNummer,
      },
    }),
  });

  const sortedPerioder = (data?.perioder ?? []).slice().sort((a, b) => {
    return (
      new Date(b.tilEksklusiv).getTime() - new Date(a.tilEksklusiv).getTime()
    );
  });

  const perioderByYear = sortedPerioder.reduce<
    Record<number, MalerdataPeriode[]>
  >((acc, periode) => {
    const year = new Date(periode.fraInklusiv).getFullYear();
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(periode);
    return acc;
  }, {});

  const sortedYears = Object.keys(perioderByYear)
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <div className="space-y-6">
      {isLoading ? (
        <Skeleton width={"600px"} height={"150px"} />
      ) : isError ? (
        <Alert data-color="danger">{t("pages.maalerdata-perioder.feil")}</Alert>
      ) : (data?.perioder.length ?? 0) < 1 ? (
        <Alert>{t("pages.maalerdata-perioder.ingen-perioder")}</Alert>
      ) : (
        sortedYears.map((year, index) => (
          <Fragment key={year}>
            {index < 1 && (
              <div>
                <Heading level={2} data-size="sm">
                  {year}
                </Heading>
                <div className="space-y-4 pt-2">
                  {perioderByYear[year].map((periode) => (
                    <MaaledataPeriodeCard
                      key={periode.fraInklusiv + periode.tilEksklusiv}
                      periode={periode}
                    />
                  ))}
                </div>
              </div>
            )}
            {index > 0 && (
              <Details key={year} defaultOpen={index === 0} variant="default">
                <Details.Summary>
                  <Heading level={2} data-size="sm">
                    {year}
                  </Heading>
                </Details.Summary>
                <Details.Content>
                  <div className="space-y-4 pt-2">
                    {perioderByYear[year].map((periode) => (
                      <MaaledataPeriodeCard
                        key={periode.fraInklusiv + periode.tilEksklusiv}
                        periode={periode}
                      />
                    ))}
                  </div>
                </Details.Content>
              </Details>
            )}
          </Fragment>
        ))
      )}
    </div>
  );
};
